import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTheme } from "@mui/material/styles";
import { Breadcrumbs, styled } from "@mui/material";
import { ExpandLess, ExpandMore, PersonRounded } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminUserDetails,
  setRoleStart,
  getNotificationTypes,
  setAdminUserData,
} from "../../redux/slices/userSlice";
import moment from "moment/moment";
import { isValidName } from "../../utils/index";
import DeleteAdminUserPopUp from "./deleteAdminUserPopup";
import ReactivateAdminUserPopUp from "./reactivateAdminUserPopup";

function UserProfile({ navigateHome }) {
  // State for selected user role
  const [userRole, setUserRole] = useState("");
  const [language, setLanguage] = useState("en");
  const [notifsAndPrefsData, setNotifsAndPrefsData] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nameErrors, setNameErrors] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
  });

  const [open, setOpen] = useState(false);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [reactivatePopUpOpen, setReactivatePopUpOpen] = useState(false);

  const dispatch = useDispatch();
  const params = useParams();

  let { adminUserData, roles, languages, notifsAndPrefs, loading } =
    useSelector((state) => state.userManagement);

  useEffect(() => {
    if (params.id) {
      dispatch(getAdminUserDetails({ admin_user_id: params.id }));
      dispatch(getNotificationTypes());
      if (!(roles.length && languages.length)) dispatch(setRoleStart());
    }
    // eslint-disable-next-line
  }, [params.id]);

  useEffect(() => {
    if (adminUserData) {
      let notifsAndPrefss = [];

      if (adminUserData?.notifs?.length)
        notifsAndPrefss = [
          ...notifsAndPrefss,
          ...adminUserData?.notifs.split(","),
        ];
      if (adminUserData?.prefs?.length)
        notifsAndPrefss = [
          ...notifsAndPrefss,
          ...adminUserData?.prefs.split(","),
        ];

      setLanguage(adminUserData.preferred_language);
      setUserRole(adminUserData.admin_user_role_id);
      setFirstName(adminUserData.admin_user_first_name);
      adminUserData?.admin_user_middle_name &&
        setMiddleName(adminUserData.admin_user_middle_name);
      setLastName(adminUserData.admin_user_last_name);
      setNotifsAndPrefsData([...notifsAndPrefss]);
    } else {
      setNotifsAndPrefsData([]);
    }

    //eslint-disable-next-line
  }, [adminUserData]);

  const theme = useTheme();
  const navigate = useNavigate();

  const CustomBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
    "& li.MuiBreadcrumbs-separator": {
      margin: 0,
    },
  }));

  const isMobile = useMediaQuery(theme.breakpoints.between("sm", "stm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("stm", "mtl"));

  // const radioRole = [
  //   "Super Admin",
  //   "Business Admin",
  //   "Business Users",
  //   "IT Admin",
  // ];

  // Handle role change
  const handleRoleChange = (event) => {
    setUserRole(event.target.value);
  };

  const formattedDate = (date) => {
    if (!date) return "";
    return moment(date).format("MMM DD, YYYY");
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleBack = () => {
    navigate("/");
    navigateHome(3, 3);
  };

  const handleNotifsAndPrefs = (e) => {
    const value = e.target.value;
    let filteredNotifsAndPrefs = [];
    const exists = notifsAndPrefsData.some((e) => e === value);
    if (exists) {
      filteredNotifsAndPrefs = notifsAndPrefsData.filter(
        (item) => item !== value
      );
    } else {
      filteredNotifsAndPrefs = [...notifsAndPrefsData, value];
    }
    setNotifsAndPrefsData([...filteredNotifsAndPrefs]);
  };

  const handleNameChange = (e, type) => {
    const value = e.target.value;
    const checkValidation = isValidName(e.target.value);

    if (type === "middleName") {
      if (!value.trim()) {
        setMiddleName(value);
        setNameErrors({
          ...nameErrors,
          middleName: "",
        });
        return;
      }

      if (!/^[a-zA-Z\s]*$/.test(value)) {
        setMiddleName(value);
        setNameErrors({
          ...nameErrors,
          middleName: "*Name should contain only alphabetic characters",
        });
      } else {
        setMiddleName(value);
        setNameErrors({
          ...nameErrors,
          middleName: "",
        });
      }
      return;
    }

    if (checkValidation.valid) {
      if (type === "firstName") {
        setFirstName(value);
        setNameErrors({
          ...nameErrors,
          firstName: "",
        });
      } else if (type === "lastName") {
        setLastName(value);
        setNameErrors({
          ...nameErrors,
          lastName: "",
        });
      }
    } else {
      if (type === "firstName") {
        setFirstName(value);

        setNameErrors({
          ...nameErrors,
          firstName: checkValidation.error,
        });
      } else if (type === "lastName") {
        setLastName(value);

        setNameErrors({
          ...nameErrors,
          lastName: checkValidation.error,
        });
      }
    }
  };

  const handleSubmit = () => {
    const data = {
      admin_user_first_name: firstName,
      admin_user_middle_name: middleName,
      admin_user_last_name: lastName,
      preferred_language: language,
      admin_user_role_id: userRole,
      user_opted_notifications: [...notifsAndPrefsData],
    };

    dispatch(
      setAdminUserData({
        data: data,
        admin_user_id: params.id,
        type: "ALL",
      })
    );
  };

  const handleDeleteUser = () => {
    setDeletePopUpOpen(true);
  };

  const handleReactivateUser = () => {
    setReactivatePopUpOpen(true);
  };


  const duplicateUserEmails = (email) => {
    const emails = email.split("__");

    if (emails.length > 1) {
      return emails[0];
    } else {
      return emails;
    }
  };

  return (
    <div>
      <Grid container spacing={2} padding={2}>
        <Grid
          container
          sx={{
            mt: "6.5rem",
            ml: isMobile ? "28px" : isTablet ? "32px" : "86px",
          }}
        >
          <CustomBreadcrumbs
            separator={
              <NavigateNextIcon fontSize="small" sx={{ color: "#00507861" }} />
            }
          >
            <Typography
              sx={{
                fontFamily: "Open Sans",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "18px",
                cursor: "pointer",
                color: "#000000",
              }}
              component="div"
              onClick={handleBack}
            >
              Manage User
            </Typography>
            <Typography
              sx={{
                fontFamily: "Open Sans",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "14px",
                color: "#000000",
              }}
            >
              {adminUserData.admin_user_first_name +
                " " +
                adminUserData.admin_user_last_name}
            </Typography>
          </CustomBreadcrumbs>
        </Grid>

        <Grid
          container
          spacing={2}
          alignItems="center"
          mb={2}
          sx={{
            mt: "20px",
            mb: "24px",
            mx: isMobile ? "12px" : isTablet ? "14px" : "68px",
            mr: isMobile || isTablet ? 0 : "78px",
          }}
        >
          <Grid item xs={12} stm={9} md={9} lg={10.3}>
            <Typography
              variant="h6"
              sx={{
                mb: "14px",
                fontFamily: "Open Sans",
                fontWeight: 700,
                fontSize: "36px",
              }}
            >
              Profile Settings
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            stm={3}
            md={3}
            lg={1.7}
            container
            justifyContent={isMobile ? "flex-start" : "flex-end"}
          >
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                onClick={adminUserData.active ? handleDeleteUser :handleReactivateUser}
                sx={{
                  textTransform: "none",
                  backgroundColor: "white",
                  borderRadius: "24px",
                  minWidth: "120px",
                  // color: "#1460AA",
                  mb: "14px",
                  border: "1px solid #1460AA",
                }}
              >
                {adminUserData.active ? "Delete User" : "Reactivate User"}
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            backgroundColor: "white",
            boxShadow: 3,
            zIndex: 1,
            borderRadius: "10px",
            mx: isMobile ? "22px" : isTablet ? "32px" : "78px",
            mr: isMobile || isTablet ? 0 : "78px",
          }}
        >
          {/* User Info Section */}
          <Grid
            item
            xs={12}
            stm={8}
            md={8}
            padding={3}
            sx={{ backgroundColor: "#EFFAFF" }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box style={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      backgroundColor: "#B8DCFF",
                      ml: "-5px",
                      mr: "16px",
                    }}
                  >
                    <PersonRounded
                      sx={{ color: "#91BACD", width: 40, height: 40 }}
                    />
                  </Avatar>
                  <IconButton
                    sx={{
                      position: "absolute",
                      bottom: 1,
                      right: 15,
                      backgroundColor: "white",
                      width: 24,
                      height: 24,
                      padding: 0,
                      borderRadius: "50%",
                      boxShadow: 2,
                      zIndex: 1,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16, color: "#78A1B4" }} />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item sx={{ ml: "-10px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: 700,
                    fontSize: "24px",
                    lineHeight: "32px",
                    color: "#1460AA",
                  }}
                >
                  {adminUserData.admin_user_first_name +
                    " " +
                    adminUserData.admin_user_last_name}
                </Typography>
                <Typography
                  color={adminUserData.active ? "#20842F" : "#D8292F"}
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: "20px",
                  }}
                >
                  {adminUserData.active ? "Active User" : "Deleted User"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            stm={4}
            textAlign={{ xs: "left", stm: "right", md: "right" }}
            padding={3}
            sx={{ backgroundColor: "#EFFAFF" }}
          >
            {isMobile && (
              <Divider
                sx={{
                  backgroundColor: "#E2E3E5",
                  ml: "-23px",
                  mr: "-23px",
                  mt: "-10px",
                  mb: "16px",
                }}
              />
            )}
            <Grid
              container
              justifyContent="space-between"
              rowGap={"10px"}
              direction={{ xs: "row", sm: "row", md: "column" }}
              spacing={{ xs: 1, md: 0 }}
            >
              <Grid item>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: "#717173",
                  }}
                >
                  Joined on: {formattedDate(adminUserData.joined_on_date)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: "#717173",
                  }}
                >
                  Last Active:{" "}
                  {adminUserData.last_active_date
                    ? formattedDate(adminUserData.last_active_date)
                    : "N/A"}
                </Typography>
              </Grid>
              {adminUserData.active ? null : (
                <Grid item>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Open Sans",
                      fontWeight: 600,
                      fontSize: "12px",
                      lineHeight: "16px",
                      color: "#717173",
                    }}
                  >
                    Deleted on:{" "}
                    {adminUserData.deleted_on_date
                      ? formattedDate(adminUserData.deleted_on_date)
                      : "N/A"}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* User Details Section */}
          <Grid item xs={12} md={12} lg={12} padding={3}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Open Sans",
                fontWeight: "700",
                fontSize: "16px",
                lineHeight: "20px",
                color: "#005078",
              }}
            >
              User Details
            </Typography>
            <Grid container spacing={2} sx={{ my: "12px" }}>
              <Grid item xs={12} stm={6} md={6} lg={4}>
                <TextField
                  variant="standard"
                  label="First Name*"
                  value={firstName ? firstName : ""}
                  fullWidth
                  sx={{
                    mb: "16px",
                  }}
                  error={nameErrors.firstName?.trim() ? true : false}
                  helperText={
                    <FormHelperText>{nameErrors.firstName}</FormHelperText>
                  }
                  onChange={(e) => {
                    handleNameChange(e, "firstName");
                  }}
                ></TextField>
              </Grid>
              <Grid item xs={12} stm={6} md={6} lg={4}>
                <TextField
                  variant="standard"
                  label="Middle Name"
                  value={middleName ? middleName : ""}
                  fullWidth
                  sx={{
                    mb: "16px",
                  }}
                  error={nameErrors.middleName?.trim() ? true : false}
                  helperText={
                    <FormHelperText>{nameErrors.middleName}</FormHelperText>
                  }
                  onChange={(e) => {
                    handleNameChange(e, "middleName");
                  }}
                ></TextField>
              </Grid>
              <Grid item xs={12} stm={6} md={6} lg={4}>
                <TextField
                  variant="standard"
                  label="Last Name*"
                  value={lastName ? lastName : ""}
                  fullWidth
                  sx={{
                    mb: "16px",
                  }}
                  error={nameErrors.lastName?.trim() ? true : false}
                  helperText={
                    <FormHelperText>{nameErrors.lastName}</FormHelperText>
                  }
                  onChange={(e) => {
                    handleNameChange(e, "lastName");
                  }}
                ></TextField>
              </Grid>
              <Grid item xs={12} stm={6} md={6} lg={4}>
                <TextField
                  disabled
                  variant="standard"
                  label="Email Address*"
                  value={
                    adminUserData.admin_user_email
                      ? duplicateUserEmails(adminUserData.admin_user_email)
                      : ""
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          {/* User Role Section with Radio Buttons */}
          <Grid item xs={12} md={12} lg={12} padding={3}>
            <Divider
              sx={{
                backgroundColor: "#E2E3E5",
                ml: "-23px",
                mr: "-23px",
              }}
            />
            <Grid container spacing={2} sx={{ mt: "32px" }}>
              <Grid
                item
                xs={12}
                stm={6}
                md={6}
                lg={4}
                sx={{ mt: "22px", mb: "32px" }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mt: "-40px",
                    fontFamily: "Open Sans",
                    fontWeight: "700",
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#005078",
                  }}
                >
                  User Roles
                </Typography>
                <FormControl fullWidth sx={{ mt: "50px" }}>
                  <Select
                    variant="standard"
                    value={userRole}
                    size="small"
                    displayEmpty
                    onChange={handleRoleChange}
                    renderValue={(selected) => {
                      const selectedRole = roles.find(
                        (role) => role.admin_user_role_id === selected
                      );
                      return selectedRole ? selectedRole.role_en : "";
                    }}
                    IconComponent={() =>
                      open ? <ExpandLess /> : <ExpandMore />
                    }
                    onOpen={handleToggle}
                    onClose={handleToggle}
                  >
                    {roles.map((role, index) => [
                      <MenuItem key={index} value={role.admin_user_role_id}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                          sx={{ mb: "-10px", mt: "-10px" }}
                        >
                          {role.role_en}
                          <Radio
                            checked={userRole === role.admin_user_role_id}
                            value={role.admin_user_role_id}
                            onChange={() => {}} // Prevent Radio from handling click
                          />
                        </Box>
                      </MenuItem>,
                      index < roles.length - 1 && (
                        <Divider sx={{ mb: "-10px" }} />
                      ),
                    ])}
                  </Select>
                </FormControl>
              </Grid>

              {/* Language Section */}
              <Grid item xs={12} md={6} stm={6} lg={4} sx={{ mt: "-20px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: "700",
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#005078",
                  }}
                >
                  Language
                </Typography>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{
                      color: "#4A4F55",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "18px",
                      pt: "15px",
                    }}
                  >
                    Preferred Language for Emails
                  </FormLabel>
                  <Select
                    variant="standard"
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                    }}
                  >
                    {languages.map((language, index) => {
                      return (
                        <MenuItem
                          value={language.preferred_language_type}
                          key={index}
                        >
                          {language.preferred_language_description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={12} lg={12} padding={3}>
            <Divider
              sx={{
                backgroundColor: "#E2E3E5",
                ml: "-23px",
                mr: "-23px",
              }}
            />
            <Grid container spacing={2} sx={{ mt: "32px" }}>
              <Grid item xs={12} sm={12}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Open Sans",
                    fontWeight: "700",
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#005078",
                    marginBottom: "20px",
                  }}
                >
                  Notification List
                </Typography>
                <FormGroup>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "100%" : "50% 50%",
                    }}
                  >
                    {notifsAndPrefs.map((notifOrPref, index) => {
                      return (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              sx={{ color: "#1460AA" }}
                              checked={notifsAndPrefsData.includes(
                                notifOrPref.preferences_type_id ||
                                  notifOrPref.notification_type_id
                              )}
                              onChange={handleNotifsAndPrefs}
                            />
                          }
                          value={
                            notifOrPref.preferences_type_id ||
                            notifOrPref.notification_type_id
                          }
                          label={
                            notifOrPref.notification_type_en ||
                            notifOrPref.preferences_type_en
                          }
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontFamily: "Open Sans",
                              fontWeight: "400",
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#000000",
                            },
                          }}
                        />
                      );
                    })}
                  </div>
                </FormGroup>
              </Grid>
            </Grid>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12} padding={3}>
            <Divider
              sx={{
                backgroundColor: "#E2E3E5",
                ml: "-23px",
                mr: "-23px",
                mb: "32px",
              }}
            />
            {!adminUserData.active ||
            nameErrors.firstName ||
            nameErrors.middleName ||
            nameErrors.lastName ? (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  textTransform: "none",
                  borderRadius: "24px",
                  padding: "12px 32px",
                }}
                disabled
                fullWidth={isMobile}
              >
                Save updates
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  textTransform: "none",
                  borderRadius: "24px",
                  padding: "12px 32px",
                  minWidth: "150px",
                }}
                fullWidth={isMobile}
                onClick={handleSubmit}
              >
                {loading ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "white" }}
                  ></CircularProgress>
                ) : (
                  "Save updates"
                )}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      {deletePopUpOpen && adminUserData.active ? (
        <DeleteAdminUserPopUp
          open={deletePopUpOpen}
          id={params.id}
          handleClose={() => {
            setDeletePopUpOpen(false);
          }}
          header={"Confirm delete User ?"}
          body={
            "Are you sure you want to delete user. Deleting user will remove all their data permanently."
          }
        ></DeleteAdminUserPopUp>
      ) : (
        <></>
      )}
      {reactivatePopUpOpen && !adminUserData.active ? (
  <ReactivateAdminUserPopUp
    open={reactivatePopUpOpen}
    id={params.id}
    handleClose={() => {
      setReactivatePopUpOpen(false);
    }}
    header={"Confirm User Reactivation"}
    body={"Are you sure you want to reactivate this user?"}
  />
) : (
  <></>
)}
 
    </div>
  );
}

export default UserProfile;
